import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { SiteLayout } from "@/components/site/layout";
import { Reveal } from "@/components/site/reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FAQ_ITEMS, SITE } from "@/lib/site";
import { useInnhold } from "@/lib/innhold";
import { faqJsonLd, localBusinessJsonLd } from "@/components/site/jsonld";
import { SPORSMAL_SIDER, sporsmalPath } from "@/lib/sporsmal";

const TITLE = "Ofte stilte spørsmål | Drammen Car Wash";
const DESC = "Svar på det du lurer på om bilvask, priser, ventetid og åpningstider hos Drammen Car Wash.";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(localBusinessJsonLd()) },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(FAQ_ITEMS)) },
    ],
  }),
  component: FaqPage,
});

function FaqPage() {
  // Tekstene eieren kan redigere fra Ascendly-appen. Se src/lib/innhold.ts.
  const t = useInnhold();
  return (
    <SiteLayout>
      <section className="pt-32 pb-16 sm:pt-40 sm:pb-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-10">
          <Reveal>
            <div className="eyebrow">{t("faq.stikktittel")}</div>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl md:text-6xl text-balance">{t("faq.tittel")}</h1>
            <p className="mt-4 text-muted-foreground max-w-xl">
              {t("faq.tekst")} {SITE.phone} eller {SITE.email}.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 sm:pb-28 md:pb-40">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-10">
          <Reveal>
            <Accordion type="single" collapsible className="rounded-2xl border border-border bg-card px-6">
              {FAQ_ITEMS.map((item) => (
                <AccordionItem key={item.q} value={item.q}>
                  <AccordionTrigger className="text-left font-serif text-lg">{item.q}</AccordionTrigger>
                  {/* forceMount: svaret ligger i HTML-en også når panelet er lukket (skjult med CSS
                      i accordion.tsx), saa AI-søk og Google leser det uten aa klikke. Utseendet er likt. */}
                  <AccordionContent forceMount className="text-muted-foreground leading-relaxed">
                    {item.a}
                    {"href" in item && item.href && (
                      <Link to={item.href as any} className="mt-3 block text-sm text-primary hover:underline">
                        Les hele svaret →
                      </Link>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          {/* Synlige lenker til sidene der hvert spørsmål har sitt eget svar. */}
          <Reveal delay={80} className="mt-12">
            <div className="eyebrow">Spørsmål med egen side</div>
            <ul className="mt-4 flex flex-col gap-2">
              {SPORSMAL_SIDER.map((side) => (
                <li key={side.slug}>
                  <Link to={sporsmalPath(side) as any} className="text-foreground/85 hover:text-primary">
                    {side.sporsmal} →
                  </Link>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={100} className="mt-10 flex justify-center">
            <Link
              to="/book"
              className="inline-flex items-center gap-2 rounded-full bg-primary-strong px-7 py-3.5 text-sm font-medium text-primary-foreground shadow-luxe lift"
            >
              {t("felles.bookknapp")} <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </SiteLayout>
  );
}
